import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenge } from 'src/hook/data/useChallenge';
import { useLearning } from 'src/hook/data/useLearning';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { PageSource } from 'src/model/enum/enums';
import { IconLocation } from 'src/utils/URLUtils';
import { Word } from '../../../model/Word';
import styles from './ChallengeFooter.module.scss';
import ReviewModal from './ReviewModal/ReviewModal';

type ChallengeFooterProps = {
    word: Word;
    source?: PageSource;
}


export const ChallengeFooter = ({ word, source }: ChallengeFooterProps) => {
    const { t } = useTranslation();
    const { getReviewCount } = useChallenge();
    const [reviewCount, setReviewCount] = useState(0);
    const { remembered } = useLearning();
    const { startClosing } = useComponentModal();
    const { show } = usePopUp();

    function getReviewCountText(count: number): string {
        switch (count) {
            case 0:
                return t("Never before");
            case 1:
                return t("Once so far");
            case 2:
                return t("Twice so far");
            default:
                return t(`${count} times`);
        }
    }

    function getReviewIntervalText(count: number): string {
        switch (count) {
            case 0:
            case 1:
                return t("Review Tomorrow");
            case 2:
                return t("Review in 2 days");
            case 3:
                return t("Review in 3 days");
            case 4:
                return t("Review in 4 days");
            case 5:
                return t("Review in a week");
            case 6:
                return t("Review in 2 weeks");
            case 7:
                return t("Review in a month");
            case 8:
                return t("Review in 2 months");
            case 9:
                return t("Review in 3 months");
            case 10:
            case 11:
                return t("Review in 6 months");
            case 12:
                return t("All Done");
            default:
                return t("Review Tomorrow");
        }
    }


    const handleReviewModal = () => {
        show({
            children: <ReviewModal
                word={word}
                reviewCount={reviewCount}
            />,
        });
    }

    useEffect(() => {
        if (source === PageSource.Challenge) {
            setReviewCount(getReviewCount(word.ID) + (source === PageSource.Challenge ? 1 : 0));
            remembered(word.ID);
        }
    }, [word.ID, source]);//dont add remembered to the dependency array

    return (

        <section className={styles.challengeFooter}>
            <div className={styles.buttonSection}>
                <div className={styles.statusIndicator}>
                    <img src={IconLocation('rounded-tick', true)}
                        alt={t("check")} className={styles.checkIcon} />
                    <div className={styles.statusTextContainer}>
                        <span className={styles.statusText}>
                            {getReviewCountText(reviewCount)}
                        </span>
                        <div className={styles.boxContainer}>
                            {[...Array(12)].map((_, index) => (
                                <div
                                    key={index}
                                    className={`${styles.box} ${index < reviewCount ? styles.filled : ''}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.reviewButton}>
                    <span onClick={startClosing}>
                        {getReviewIntervalText(reviewCount)}</span>
                    <div className={styles.dropdownArrow}
                        onClick={handleReviewModal}
                    >
                        <img src={IconLocation('chevron-down', true)}
                            alt={t("arrow down")} />
                    </div>
                </div>

            </div>
        </section>

    );
};
